import * as R from "ramda";
import axios from "axios";

import { BATCH_ACTIONS_KEYS, CANDIDATE_TYPES } from "../constants/opportunity";
import { createQueryString } from "../utils/queryStrings";
import { ACCEPT_LANGUAGE } from "../locales/acceptedLanguages";

import { CANDIDATES_PER_PAGE } from "./candidatesFilters";

const isDemoActive = localStorage.getItem("IS_DEMO_ACTIVE");

const normalizeMatchesResponse = data =>
  Array.isArray(data)
    ? { candidates: data, pagination: null }
    : {
        candidates: R.propOr([], "candidates", data),
        pagination: R.propOr(null, "pagination", data)
      };

// Close split mock data
// https://facebook.github.io/create-react-app/docs/code-splitting
const loadDemoData = (...params) =>
  import("./mocks/getDemoData").then(({ default: getDemoData }) =>
    getDemoData(...params)
  );

// Turn true to use Production Environment
const isProd = false;

let BASE_URL = isProd
  ? "https://api2.worcket.com/"
  : process.env.REACT_APP_API_URL;
let NEW_BASE_URL = isProd
  ? "https://api.worcket.com/v1/"
  : process.env.REACT_APP_NEW_API_URL;

const DEFAULT_OPTIONS = {
  method: "get",
  crossDomain: true,
  responseType: "json",
  headers: {
    "Content-Type": "application/json"
  }
};

const env = R.pipe(
  R.defaultTo(""),
  R.toUpper
)(localStorage.getItem("environment"));

if (
  process.env.REACT_APP_ENVIRONMENT !== "PROD" &&
  env &&
  process.env[`REACT_APP_${env}_API_URL`] &&
  process.env[`REACT_APP_NEW_${env}_API_URL`]
) {
  BASE_URL = isProd
    ? "https://api2.worcket.com/"
    : process.env[`REACT_APP_${env}_API_URL`];
  NEW_BASE_URL = isProd
    ? "https://api.worcket.com/v1/"
    : process.env[`REACT_APP_NEW_${env}_API_URL`];
}

const api = {
  getBaseUrl: function() {
    return BASE_URL;
  },
  request: async function({
    url,
    data = {},
    useNewApi = false,
    headers,
    ...rest
  }) {
    try {
      const mockData = isDemoActive && (await loadDemoData({ url, data }));

      if (mockData) {
        return { data: mockData };
      }

      const response = await axios({
        ...DEFAULT_OPTIONS,
        url: (useNewApi ? NEW_BASE_URL : BASE_URL) + url,
        data: data,
        headers: {
          ...DEFAULT_OPTIONS.headers,
          Authorization: `${localStorage.getItem("token")}`,
          ...headers
        },
        ...rest
      });

      if (response.status >= 400) throw response;

      return { data: response.data };
    } catch (error) {
      if (R.pathEq(["response", "status"], 403, error)) {
        localStorage.removeItem("token");
        localStorage.removeItem("selected_company_id");
        window.location.reload();
      }

      return { error };
    }
  },
  batchAction: async function({
    action,
    candidates,
    type,
    idCompany,
    idOpportunity,
    emptyBody,
    payload
  }) {
    // TODO whenever we need a custom payload for an action, we add said action to the array
    const customPayloadForAction = R.includes(action, [
      BATCH_ACTIONS_KEYS.moveToStep
    ]);

    if (
      R.and(customPayloadForAction, R.not(R.equals(typeof payload, "object")))
    ) {
      throw new Error(
        `If you use the "${action}" action, you need to pass a payload in the key "payload"`
      );
    }

    const externalAction = R.and(
      R.equals(type, CANDIDATE_TYPES.external.type),
      R.not(customPayloadForAction)
    );
    const task = R.and(
      externalAction,
      R.includes(action, [
        BATCH_ACTIONS_KEYS.rejectExternal,
        BATCH_ACTIONS_KEYS.likeExternal
      ])
    )
      ? BATCH_ACTIONS_KEYS.moveExternal
      : action;

    const { error, data } = await api.request({
      useNewApi: true,
      url: "croupier",
      method: "post",
      data: {
        task,
        payload: candidates.map(idCandidate => {
          const body = R.and(customPayloadForAction, payload)
            ? payload
            : R.equals(task, BATCH_ACTIONS_KEYS.moveExternal)
            ? { enabled: action === BATCH_ACTIONS_KEYS.likeExternal }
            : { idCandidate };
          const pathParameters = customPayloadForAction
            ? {
                idCandidate,
                idCompany,
                idOpportunity
              }
            : externalAction
            ? {
                idCandidate,
                idOpportunity
              }
            : {
                idCompany,
                idOpportunity
              };
          return emptyBody ? { pathParameters } : { body, pathParameters };
        })
      }
    });

    if (error) {
      throw new Error(error);
    }

    return data;
  },
  team: {
    list: {
      fetch: async function(company) {
        const { data, error } = await api.request({
          url: `company/${company}/team`
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      }
    },
    update: async function(company, team, formData) {
      const { error, data } = await api.request({
        url: `company/${company}/team/${team}`,
        method: "patch",
        data: formData
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    create: async function(company, formData) {
      const { error, data } = await api.request({
        url: `company/${company}/team`,
        method: "post",
        data: formData
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    exportCandidates: async function(idTeam, idCompany) {
      const { error, data } = await api.request({
        url: `company/${idCompany}/team/${idTeam}/candidates/export`,
        method: "get",
        useNewApi: true,
        responseType: "blob"
      });
      if (error) {
        throw new Error(error);
      }
      return data;
    }
  },
  opportunities: {
    fetch: async function(company) {
      const { data, error } = await api.request({
        url: `company/${company}/opportunity`
      });

      if (error) {
        throw new Error(error);
      }

      return R.pipe(
        R.reject(R.propEq("status", "closed")),
        // Show opportunities with team=null in opportunities list
        R.map(R.when(R.propEq("team", null), R.dissoc("team"))),
        R.map(R.when(R.propEq("finished", true), R.assoc("status", "finished")))
      )(data);
    },

    notifications: async function(company) {
      const { error, data } = await api.request({
        url: `company/${company}/opportunities/notifications`
      });

      if (error) {
        throw new Error(error);
      }

      return R.pipe(
        R.filter(R.has("idOpportunity")),
        R.map(
          ({
            newMessages: messages,
            newLikes: candidates,
            newMatches: matches,
            idOpportunity
          }) => ({
            [idOpportunity]: { messages, candidates, matches }
          })
        ),
        R.mergeAll
      )(data);
    },
    resetNotifications: async function(opportunityId, companyId, type) {
      if (!type || (type !== "like" && type !== "match")) {
        throw new Error("Type is not valid");
      }

      const { error, data } = await api.request({
        url: `company/${companyId}/opportunity/${opportunityId}/notifications`,
        method: "post",
        data: {
          type
        }
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    }
  },
  opportunity: {
    fetch: async function(id, company) {
      if (!company) {
        throw new Error("Company is not defined");
      }

      const { error, data } = await api.request({
        url: `company/${company}/opportunity/${id}`
      });

      if (error) {
        throw new Error(error);
      }

      return R.pipe(
        R.evolve({
          languages: R.pipe(
            R.defaultTo([]),
            R.filter(
              R.pipe(
                R.propOr("", "name"),
                R.trim,
                R.isEmpty,
                R.not
              )
            )
          )
        }),
        R.when(R.propEq("finished", true), R.assoc("status", "finished"))
      )(data);
    },
    matches: {
      fetch: async function(idCompany, idOpportunity, options = {}) {
        const query = options.page
          ? `?${createQueryString(
              R.reject(R.isNil, {
                page: options.page,
                limit: options.limit,
                stepId: options.stepId
              })
            )}`
          : "";
        const { error, data } = await api.request({
          method: "get",
          url: `company/${idCompany}/opportunity/${idOpportunity}/matches${query}`
        });

        if (error) {
          throw new Error(error);
        }

        const matchesResponse = normalizeMatchesResponse(data);

        return options.page ? matchesResponse : matchesResponse.candidates;
      },
      step: {
        update: async function(idCompany, idOpportunity, idCandidate, step) {
          const { error, data } = await api.request({
            method: "patch",
            url: `company/${idCompany}/opportunity/${idOpportunity}/matches/${idCandidate}/step`,
            data: {
              step
            }
          });

          if (error) {
            throw R.path(["response", "data", "message"], error) || error;
          }

          return data;
        }
      }
    },
    update: async function(opportunityId, companyId, formData) {
      const { error, data } = await api.request({
        url: `company/${companyId}/opportunity/${opportunityId}`,
        method: "patch",
        data: formData
      });

      if (error) {
        throw new Error(error);
      }

      return R.when(R.propEq("finished", true), R.assoc("status", "finished"))(
        data
      );
    },
    create: async function(formData, company) {
      const { error, data } = await api.request({
        url: `company/${company}/opportunity`,
        method: "post",
        data: formData
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    publish: async function(opportunityId, companyId, feed) {
      const { error, data } = await api.request({
        url: `company/${companyId}/opportunity/${opportunityId}/publish/${feed}`,
        method: "POST"
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    uploadDocument: async function(opportunityId, companyId, file) {
      const formData = new FormData();
      formData.append("document", file);

      const { error, data } = await api.request({
        url: `company/${companyId}/opportunity/${opportunityId}/document`,
        method: "POST",
        data: formData,
        useNewApi: true
      });

      if (error) {
        return Promise.reject(error);
      }

      return Promise.resolve(data);
    },
    markCandidatesAsRead: async function(
      idOpportunity,
      idCompany,
      candidates,
      view
    ) {
      const { error, data } = await api.request({
        url: `company/${idCompany}/opportunity/${idOpportunity}/views`,
        method: "POST",
        data: {
          candidates,
          view
        },
        useNewApi: true
      });

      if (error) {
        return Promise.reject(error);
      }

      return Promise.resolve(data);
    },
    exportCandidates: async function(idOpportunity, idCompany) {
      const { error, data } = await api.request({
        url: `company/${idCompany}/candidates/export/opportunity/${idOpportunity}`,
        method: "get",
        useNewApi: true,
        responseType: "blob"
      });
      if (error) {
        throw new Error(error);
      }
      return data;
    },
    updateCandidateTags: async function(
      idOpportunity,
      idCompany,
      candidates = [],
      addTags = [],
      removeTags = []
    ) {
      const data = {
        candidates,
        addTags,
        removeTags
      };
      const { error } = await api.request({
        url: `company/${idCompany}/opportunity/${idOpportunity}/tags`,
        method: "POST",
        data: data,
        useNewApi: true
      });
      if (error) {
        throw new Error(error);
      }
      return data;
    },
    candidates: {
      fetchCandidate: async function(idCompany, idOpportunity, idCandidate) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/candidates/${idCandidate}`,
          useNewApi: true,
          method: "get"
        });

        if (error) {
          throw new Error(error);
        }
        return data;
      },
      fetchDocumentSubmissions: async function(
        idCompany,
        idOpportunity,
        idCandidate
      ) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/candidates/${idCandidate}/document-submissions`,
          useNewApi: true,
          method: "get"
        });

        if (error) {
          throw new Error(error);
        }
        return data;
      },
      fetchSuggested: async function(
        companyId,
        opportunityId,
        { limit = CANDIDATES_PER_PAGE, filters }
      ) {
        if (!companyId || !opportunityId) {
          throw new Error("Company or opportunity are not defined");
        }

        const url = `company/${companyId}/opportunity/${opportunityId}/suggested?${createQueryString(
          {
            ...filters,
            limit
          }
        )}`;

        const { error, data } = await api.request({
          url,
          method: "get",
          useNewApi: true
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      fetchHunting: async function(
        companyId,
        opportunityId,
        { limit = CANDIDATES_PER_PAGE, filters }
      ) {
        if (!companyId || !opportunityId) {
          throw new Error("Company or opportunity are not defined");
        }

        const url = `company/${companyId}/opportunity/${opportunityId}/hunting?${createQueryString(
          {
            ...filters,
            limit
          }
        )}`;

        const { error, data } = await api.request({
          url,
          method: "get",
          useNewApi: true
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      fetchSelected: async function(companyId, opportunityId) {
        if (!companyId || !opportunityId) {
          throw new Error("Company or opportunity are not defined");
        }

        const { error, data } = await api.request({
          url: `company/${companyId}/opportunity/${opportunityId}/selected`,
          method: "get"
        });

        if (error) {
          throw new Error(error);
        }

        return R.defaultTo([], data);
      },
      fetchRejected: async function(companyId, opportunityId) {
        if (!companyId || !opportunityId) {
          throw new Error("Company or opportunity are not defined");
        }

        const { error, data } = await api.request({
          url: `company/${companyId}/opportunity/${opportunityId}/rejected`,
          method: "get"
        });

        if (error) {
          throw new Error(error);
        }

        return R.defaultTo([], data);
      },
      like: async function(idCompany, idOpportunity, idCandidate) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/like`,
          method: "post",
          data: { idCandidate }
        });

        if (error) {
          throw error;
        }

        return data;
      },
      sendScreeningReminder: async function(
        idCompany,
        idOpportunity,
        idCandidate
      ) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/send-screening-reminder`,
          method: "post",
          data: { idCandidate }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      requestCvUpdate: async function(idCompany, idOpportunity, idCandidate) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/candidate/${idCandidate}/request-cv-update`,
          method: "post"
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      superlike: async function(idCompany, idOpportunity, idCandidate) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/superlike`,
          method: "post",
          data: { idCandidate }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      moveToProcess: async function(idCompany, idOpportunity, idCandidate) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/move-to-process`,
          method: "post",
          data: { idCandidate }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      dislike: async function(idCompany, idOpportunity, idCandidate) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/dislike`,
          method: "post",
          data: { idCandidate }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      unmatch: async function(idCompany, idOpportunity, idCandidate) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/unmatch`,
          method: "post",
          data: { idCandidate }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      moveExternal: async function(
        idCompany,
        idOpportunity,
        idCandidate,
        enabled
      ) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/candidate/${idCandidate}/move-external`,
          method: "post",
          data: { enabled }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      externalLike: async function(idCompany, idOpportunity, idCandidate) {
        return api.opportunity.candidates.moveExternal(
          idCompany,
          idOpportunity,
          idCandidate,
          true
        );
      },
      externalDislike: async function(idCompany, idOpportunity, idCandidate) {
        return api.opportunity.candidates.moveExternal(
          idCompany,
          idOpportunity,
          idCandidate,
          false
        );
      },
      inviteExternalCandidate: async function(
        idCompany,
        idOpportunity,
        idCandidate
      ) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/candidate/${idCandidate}/send-external-invitation`,
          method: "post"
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      fetchMatch: async function(idCompany, idOpportunity, idCandidate) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/matches/${idCandidate}`,
          method: "get",
          useNewApi: true
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      fetchExternalMatch: async function(
        idCompany,
        idOpportunity,
        idCandidate
      ) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/external-match/${idCandidate}`,
          method: "get"
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      updateMatchNotes: async function(
        idCompany,
        idOpportunity,
        idStep,
        id,
        formData
      ) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/process/${idStep}/note/${id}`,
          method: "PATCH",
          data: formData
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      createMatchNote: async function(
        idCompany,
        idOpportunity,
        idStep,
        formData
      ) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/process/${idStep}/note`,
          method: "POST",
          data: formData
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      updateMatchStep: async function(
        idCompany,
        idOpportunity,
        idCandidate,
        step
      ) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/matches/${idCandidate}/step`,
          method: "patch",
          data: { step }
        });

        if (error) {
          throw R.path(["response", "data", "message"], error) || error;
        }

        return data;
      },
      sendOffer: async function(
        idOpportunity,
        idCandidate,
        idCompany,
        formData
      ) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/sendoffer/${idCandidate}`,
          method: "post",
          data: formData
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      shareCandidate: async function(idOpportunity, idCandidate, idCompany) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/send-opp-invitation`,
          method: "post",
          data: {
            idCandidate
          }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      fetchCandidatesBase: async function(
        companyId,
        { filters, limit = CANDIDATES_PER_PAGE }
      ) {
        if (!companyId) {
          throw new Error("Company or opportunity are not defined");
        }

        const url = `company/${companyId}/hunting?${createQueryString({
          ...filters,
          limit
        })}`;

        const { error, data } = await api.request({
          url,
          method: "get",
          useNewApi: true
        });
        if (error) {
          throw new Error(error);
        }

        return data;
      }
    },
    process: {
      fetch: async function(idCompany, idOpportunity) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/process`,
          method: "GET"
        });

        if (error) {
          throw new Error(error);
        }
        return data;
      },
      addStep: async function(idCompany, idOpportunity, step) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/process`,
          method: "post",
          data: step
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      deleteStep: async function(idCompany, idOpportunity, idStep) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/process/${idStep}`,
          method: "delete"
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      updateSteps: async function(idCompany, idOpportunity, steps) {
        const { error, data } = await api.request({
          url: `company/${idCompany}/opportunity/${idOpportunity}/process`,
          method: "patch",
          data: steps
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      },
      step: {
        fetch: async function(idCompany, idOpportunity, idStep) {
          const { error, data } = await api.request({
            url: `company/${idCompany}/opportunity/${idOpportunity}/process/${idStep}`,
            method: "get"
          });

          if (error) {
            throw new Error(error);
          }

          return data;
        },
        update: async function(idCompany, idOpportunity, idStep, formData) {
          const { error, data } = await api.request({
            url: `company/${idCompany}/opportunity/${idOpportunity}/process/${idStep}`,
            method: "PATCH",
            data: formData
          });

          if (error) {
            throw new Error(error);
          }

          return data;
        },
        room: {
          list: async function(idCompany, idOpportunity, idStep) {
            const { error, data } = await api.request({
              url: `company/${idCompany}/opportunity/${idOpportunity}/process/${idStep}/rooms`,
              method: "GET"
            });

            if (error) {
              throw new Error(error);
            }

            return data;
          },
          create: async function(idCompany, idOpportunity, idStep, formData) {
            const { error, data } = await api.request({
              url: `company/${idCompany}/opportunity/${idOpportunity}/process/${idStep}/rooms`,
              method: "POST",
              data: formData
            });

            if (error) {
              throw new Error(error);
            }

            return data;
          },
          update: async function(idCompany, idOpportunity, idStep, formData) {
            const { error, data } = await api.request({
              url: `company/${idCompany}/opportunity/${idOpportunity}/process/${idStep}/rooms`,
              method: "PATCH",
              data: formData
            });

            if (error) {
              throw new Error(error);
            }

            return data;
          },
          delete: async function(idCompany, idOpportunity, idStep, idRoom) {
            const { error, data } = await api.request({
              url: `company/${idCompany}/opportunity/${idOpportunity}/process/${idStep}/rooms/${idRoom}`,
              method: "DELETE"
            });

            if (error) {
              throw new Error(error);
            }

            return data;
          }
        }
      }
    }
  },
  candidates: {
    getMatches: async function(companyId, opportunityId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/opportunity/${opportunityId}/matches`,
        method: "get"
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    }
  },
  candidate: {
    fetch: async function(id, companyId, opportunityId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/opportunity/${opportunityId}/matches`,
        method: "get"
      });

      if (error) {
        throw new Error(error);
      }

      return R.find(R.propEq("id", id), data);
    },
    fetchCandidateByID: async function(id, companyId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/candidate/${id}`,
        method: "get",
        useNewApi: true
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    updateCandidateByID: async function(id, companyId, formData) {
      const { error, data } = await api.request({
        url: `company/${companyId}/candidate/${id}`,
        useNewApi: true,
        method: "patch",
        data: formData
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    }
  },
  chat: {
    getConnectionData: async function(companyId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/talk`,
        method: "get"
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    fetchCalendarData: async function(
      idCompany,
      idOpportunity,
      idCandidate,
      idStep
    ) {
      const { error, data } = await api.request({
        url: `company/${idCompany}/opportunity/${idOpportunity}/candidate/${idCandidate}/step/${idStep}/calendar`,
        method: "get"
      });

      if (error) {
        const message = R.pathOr("Error", ["response", "data", "message"])(
          error
        );

        throw message;
      }

      return data;
    }
  },
  messages: {
    fetchMessages: async function(companyId, candidate, opportunityId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/opportunity/${opportunityId}/talk/${candidate}`,
        method: "get"
      });
      if (error) {
        throw new Error(error);
      }
      return data.messages;
    },

    unreadMessages: async function(idCompany) {
      const { error, data } = await api.request({
        url: `company/${idCompany}/opportunities/messages`,
        method: "get"
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },

    sendMessage: async function(msg, companyId, candidate, opportunityId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/opportunity/${opportunityId}/talk`,
        method: "post",
        data: {
          text: msg,
          topic: `/${companyId}/${opportunityId}/${candidate}`
        }
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },

    read: async function(idOpportunity, idCandidate, idCompany) {
      const { error, data } = await api.request({
        url: `company/${idCompany}/opportunity/${idOpportunity}/talk/read`,
        method: "post",
        data: { topic: `/${idCompany}/${idOpportunity}/${idCandidate}` }
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },

    sendFile: async function(companyId, opportunityId, candidateId, file) {
      const formData = new FormData();
      formData.append("document", file);

      const { error, data } = await api.request({
        url: `company/${companyId}/opportunity/${opportunityId}/candidate/${candidateId}/document`,
        method: "POST",
        data: formData,
        useNewApi: true
      });

      if (error) {
        return Promise.reject(error);
      }

      return Promise.resolve(data);
    }
  },
  company: {
    fetch: async function(id) {
      const { error, data } = await api.request({
        url: `company/${id}`,
        method: "get"
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    update: async function({ id, ...formData }) {
      const { error, data } = await api.request({
        url: `company/${id}`,
        method: "patch",
        data: formData
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    getUsers: async function(companyId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/users`,
        method: "get"
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    inviteUser: async function(companyId, inviteData) {
      const { error, data } = await api.request({
        url: `company/${companyId}/users/signup-invite`,
        method: "post",
        data: inviteData
      });
      if (error) {
        throw new Error(error);
      }
      return data;
    },
    deleteUser: async function(companyId, userId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/users/${userId}`,
        method: "delete"
      });
      if (error) {
        throw new Error(error);
      }
      return data;
    },
    exportCandidates: async function(companyId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/candidates/export`,
        method: "get",
        useNewApi: true,
        responseType: "blob"
      });
      if (error) {
        throw new Error(error);
      }
      return data;
    },
    social: {
      create: async function(companyId, provider, options) {
        const { error, data } = await api.request({
          url: `company/${companyId}/social`,
          method: "post",
          data: { socialAccounts: { [provider]: options } }
        });
        if (error) {
          throw error;
        }
        return data;
      },
      delete: async function(companyId, provider) {
        const { error, data } = await api.request({
          url: `company/${companyId}/social/${provider}`,
          method: "delete"
        });
        if (error) {
          throw new Error(error);
        }
        return data;
      }
    },
    linkCompanies: async function(companyId, vinculations) {
      const { error, data } = await api.request({
        url: `company/${companyId}/link-to-company`,
        method: "post",
        data: vinculations
      });
      if (error) {
        return Promise.reject(error);
      }
      return data;
    }
  },
  headquarter: {
    company: {
      list: {
        fetch: async function(superCompanyId) {
          const { error, data } = await api.request({
            url: `headquarter/${superCompanyId}/companies`,
            method: "get"
          });

          if (error) {
            throw new Error(error);
          }

          return data;
        },
        addCompany: async function(superCompanyId, formData) {
          const { error, data } = await api.request({
            url: `headquarter/${superCompanyId}/companies`,
            method: "post",
            data: formData
          });
          if (error) {
            throw error;
          }

          return data;
        }
      }
    }
  },
  session: {
    login: async function(formData) {
      const cleanEmail = formData.emailAddress
        .trim()
        .replace(/[^\x00-\x7F]/g, ""); //eslint-disable-line

      const { error, data } = await api.request({
        url: "company/login",
        method: "post",
        data: { ...formData, emailAddress: cleanEmail }
      });

      if (error) {
        throw error;
      }

      return data;
    }
  },
  user: {
    fetch: async function({ company, user }) {
      const { error, data } = await api.request({
        url: `company/${company}/users/${user}`,
        method: "get"
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    signup: async function(formData) {
      const { error, data } = await api.request({
        url: "users",
        method: "post",
        data: formData
      });

      if (error) {
        return Promise.reject(error);
      }

      return data;
    },
    update: async function({ company, user, ...formData }) {
      const { error, data } = await api.request({
        url: `company/${company}/users/${user}`,
        method: "patch",
        data: formData
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    changePassword: async function({ companyId, userId, ...formData }) {
      const { error, data } = await api.request({
        url: `company/${companyId}/users/${userId}/change`,
        method: "patch",
        data: formData
      });

      if (error) {
        throw new Error(error);
      }

      return data;
    },
    sendRecoverPasswordEmail: async function(email) {
      const { error, data } = await api.request({
        url: "users/reset-invite",
        method: "post",
        data: { email }
      });

      if (error) {
        return Promise.reject(error);
      }

      return data;
    },
    changeRecoverPassword: async function(formData) {
      const { error, data } = await api.request({
        url: "users/reset",
        method: "patch",
        data: formData
      });

      if (error) {
        return Promise.reject(error);
      }

      return data;
    },
    validateRecoverPasswordCode: async function(code) {
      const { error, data } = await api.request({
        url: "users/reset/validate",
        method: "get",
        params: { code }
      });

      if (error) {
        return Promise.reject(error);
      }

      return data;
    },
    social: {
      create: async function(companyId, userId, provider, options) {
        const { error, data } = await api.request({
          url: `company/${companyId}/users/${userId}/social`,
          method: "post",
          data: { socialAccounts: { [provider]: options } }
        });
        if (error) {
          throw error;
        }
        return data;
      },
      delete: async function(companyId, userId, provider) {
        const { error, data } = await api.request({
          url: `company/${companyId}/users/${userId}/social/${provider}`,
          method: "delete"
        });
        if (error) {
          throw new Error(error);
        }
        return data;
      }
    }
  },
  catalogs: {
    professions: {
      fetch: async function(country, locale) {
        const countryQuery = createQueryString({
          countries: R.unless(Array.isArray, R.of, country)
        });
        const { error, data } = await api.request({
          url: `public/professions?${countryQuery}`,
          headers: {
            "Accept-Language":
              ACCEPT_LANGUAGE[locale] || ACCEPT_LANGUAGE.DEFAULT
          }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      }
    },
    skills: {
      fetch: async function({ country, professions = [], locale }) {
        const query = createQueryString({
          countries: R.unless(Array.isArray, R.of, country),
          professions
        });

        const { error, data = [] } = await api.request({
          url: `public/skills?${query}`,
          method: "get",
          headers: {
            "Accept-Language":
              ACCEPT_LANGUAGE[locale] || ACCEPT_LANGUAGE.DEFAULT
          }
        });

        if (error) {
          throw new Error(error);
        }

        return R.uniq(data);
      }
    },
    countries: {
      fetch: async function(locale) {
        const { error, data } = await api.request({
          url: "public/countries",
          headers: {
            "Accept-Language":
              ACCEPT_LANGUAGE[locale] || ACCEPT_LANGUAGE.DEFAULT
          }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      }
    },
    provinces: {
      fetch: async function(countries) {
        const { error, data } = await api.request({
          useNewApi: true,
          url: "provincias",
          method: "post",
          data: { data: countries }
        });

        if (error) {
          return Promise.reject(error);
        }

        return Promise.resolve(data);
      }
    },
    districts: {
      fetch: async function(provinces) {
        const { error, data } = await api.request({
          useNewApi: true,
          url: "localidades",
          method: "post",
          data: { data: provinces }
        });

        if (error) {
          return Promise.reject(error);
        }

        return Promise.resolve(data);
      }
    },
    universities: {
      fetch: async function(country = []) {
        const countryQuery = createQueryString({
          countries: R.unless(Array.isArray, R.of, country)
        });
        const { error, data } = await api.request({
          url: `public/universities?${countryQuery}`
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      }
    },
    companies: {
      universities: async function() {
        const { error, data } = await api.request({
          url: `company/list?type=university`,
          method: "get"
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      }
    },
    tests: {
      fetch: async function(idCompany) {
        const { error, data = [] } = await api.request({
          url: `company/${idCompany}/tests`,
          method: "get"
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      }
    },
    fieldsOfStudy: {
      fetch: async function(locale) {
        const { error, data = [] } = await api.request({
          url: `public/fieldsofstudy`,
          method: "get",
          headers: {
            "Accept-Language":
              ACCEPT_LANGUAGE[locale] || ACCEPT_LANGUAGE.DEFAULT
          }
        });

        if (error) {
          throw new Error(error);
        }

        return data;
      }
    }
  },
  analytics: {
    candidates: {
      opps: {
        team: async function(idCompany, idTeam) {
          const { error, data } = await api.request({
            url: `analytics/candidatesbyopps/company/${idCompany}/team/${idTeam}`,
            method: "get"
          });

          if (error) {
            throw new Error(error);
          }

          return data;
        },
        opportunity: async function(idCompany, idOpportunity) {
          const { error, data } = await api.request({
            url: `analytics/candidatesbyopps/company/${idCompany}/opportunity/${idOpportunity}`,
            method: "get"
          });

          if (error) {
            throw new Error(error);
          }

          return data;
        },
        company: async function(idCompany) {
          const { error, data } = await api.request({
            url: `analytics/candidatesbyopps/company/${idCompany}`,
            method: "get"
          });
          if (error) {
            throw new Error(error);
          }

          return data;
        }
      },
      professions: {
        company: async function(idCompany) {
          const { error, data } = await api.request({
            url: `analytics/candidatesforprofession/company/${idCompany}`,
            method: "get"
          });
          if (error) {
            throw new Error(error);
          }

          return data;
        },
        opportunity: async function(idCompany, idOpportunity) {
          const { error, data } = await api.request({
            url: `analytics/candidatesforprofession/company/${idCompany}/opportunity/${idOpportunity}`,
            method: "get"
          });
          if (error) {
            throw new Error(error);
          }

          return data;
        },
        team: async function(idCompany, idTeam) {
          const { error, data } = await api.request({
            url: `analytics/candidatesforprofession/company/${idCompany}/team/${idTeam}`,
            method: "get"
          });
          if (error) {
            throw new Error(error);
          }

          return data;
        }
      }
    },
    opportunities: {
      company: async function(idCompany) {
        const { error, data } = await api.request({
          url: `analytics/oppsbycompany/company/${idCompany}`,
          method: "get"
        });
        if (error) {
          throw new Error(error);
        }

        return data;
      },
      team: async function(idCompany, idTeam) {
        const { error, data } = await api.request({
          url: `analytics/oppsbycompany/company/${idCompany}/team/${idTeam}`,
          method: "get"
        });
        if (error) {
          throw new Error(error);
        }
        return data;
      }
    },
    averageTime: {
      company: async function(idCompany) {
        const { error, data } = await api.request({
          url: `analytics/averagetime/company/${idCompany}`,
          method: "get"
        });
        if (error) {
          throw new Error(error);
        }

        return data;
      },
      team: async function(idCompany, idTeam) {
        const { error, data } = await api.request({
          url: `analytics/averagetime/company/${idCompany}/team/${idTeam}`,
          method: "get"
        });
        if (error) {
          throw new Error(error);
        }

        return data;
      },
      opportunity: async function(idCompany, idOpportunity) {
        const { error, data } = await api.request({
          url: `analytics/averagetime/company/${idCompany}/opportunity/${idOpportunity}`,
          method: "get"
        });
        if (error) {
          throw new Error(error);
        }

        return data;
      }
    }
  },
  chatbot: {
    isCreated: async function(idChatbot) {
      const { error, data } = await api.request({
        url: `chatbot/${idChatbot}/exists`,
        method: "GET",
        useNewApi: true
      });
      if (error) {
        throw new Error(error);
      }
      return data;
    },
    message: {
      send: async function({ message, idChatbot, idUser }) {
        const { error, data } = await api.request({
          url: `chatbot/${idChatbot}/users/${idUser}/test/chat`,
          method: "POST",
          data: { message }
        });
        if (error) {
          throw new Error(error);
        }
        return data;
      },
      init: async function({ idChatbot, idUser }) {
        const { error, data } = await api.request({
          url: `chatbot/${idChatbot}/users/${idUser}/test/init`,
          method: "POST"
        });
        if (error) {
          throw new Error(error);
        }
        return data;
      }
    }
  },
  jobOffer: {
    fetchStatus: async function(idOffer, idOpportunity, idCompany) {
      const { error, data } = await api.request({
        url: `company/${idCompany}/opportunity/${idOpportunity}/offerletter/${idOffer}/status`
      });

      if (error) {
        return Promise.reject(error);
      }

      return Promise.resolve(data);
    }
  },
  purchases: {
    fetch: async function(companyId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/purchases`,
        method: "GET"
      });

      if (error) {
        return Promise.reject(error);
      }

      return Promise.resolve(data);
    },
    fetchLite: async function(companyId) {
      const { error, data } = await api.request({
        url: `company/${companyId}/purchases/lite`,
        method: "get"
      });
      if (error) {
        throw new Error(error);
      }
      return data;
    },
    purchaseTest: async function(
      idCompany,
      idOpportunity,
      idCandidate,
      idTest
    ) {
      const { error, data } = await api.request({
        url: `company/${idCompany}/opportunity/${idOpportunity}/purchase`,
        method: "post",
        data: {
          article: "test-gh",
          idCandidate,
          idTest
        }
      });
      if (error) {
        throw new Error(error);
      }
      return data;
    }
  }
};

export default api;
